(function () {
  'use strict';

  function initializeCbbDesignStudio() {
    const intake = document.getElementById(
      'cbb-design-intake'
    );

    if (
      !intake ||
      intake.dataset.initialized === 'true'
    ) {
      return;
    }

    intake.dataset.initialized = 'true';

    // ========================================
    // Step Configuration
    // ========================================

    const STEPS = Object.freeze({
      BOARDS: 1,
      DESIGN: 2,
      UPLOADS: 3,
      CONTACT: 4,
      REVIEW: 5
    });

    // ========================================
    // Cached Elements
    // ========================================

    const message = intake.querySelector(
      '#cbb-message'
    );

    const currentStepNumber = intake.querySelector(
      '#cbb-current-step-number'
    );

    const steps = intake.querySelectorAll(
      '.cbb-step'
    );

    const progressItems = intake.querySelectorAll(
      '.cbb-progress-item'
    );

    const progressLines = intake.querySelectorAll(
      '.cbb-progress-line'
    );

    const boardOptions = intake.querySelectorAll(
      'input[name="board_configuration"]'
    );

    const boardOptionsFieldset = intake.querySelector(
      '.cbb-board-options'
    );

    const saveDesignButton = intake.querySelector(
      '#cbb-save-design'
    );

    const uploadStatus = intake.querySelector(
      '#cbb-upload-status'
    );

    const designStyleInput = intake.querySelector(
      '#cbb-design-style'
    );

    const colorPreferencesInput = intake.querySelector(
      '#cbb-color-preferences'
    );

    const textToIncludeInput = intake.querySelector(
      '#cbb-text-to-include'
    );

    const boardOneNotesInput = intake.querySelector(
      '#cbb-board1-notes'
    );

    const boardTwoNotesInput = intake.querySelector(
      '#cbb-board2-notes'
    );

    const specialInstructionsInput = intake.querySelector(
      '#cbb-special-instructions'
    );

    const customerNameInput = intake.querySelector(
      '#cbb-customer-name'
    );

    const customerEmailInput = intake.querySelector(
      '#cbb-customer-email'
    );

    const boardSizeConfirmedInput = intake.querySelector(
      '#cbb-board-size-confirmed'
    );

    const confirmationBox = intake.querySelector(
      '.cbb-confirmation-box'
    );

    // ========================================
    // General Utilities
    // ========================================

    function clearMessage() {
      if (!message) {
        return;
      }

      message.textContent = '';
      message.classList.remove('is-visible');
    }

    function showMessage(text) {
      if (!message) {
        return;
      }

      message.textContent = text;
      message.classList.add('is-visible');
    }

    function setSaveButtonState(
      text,
      loading
    ) {
      if (!saveDesignButton) {
        return;
      }

      const label =
        saveDesignButton.querySelector(
          '.cbb-button-label'
        );

      if (label) {
        label.textContent = text;
      }

      saveDesignButton.classList.toggle(
        'is-loading',
        Boolean(loading)
      );

      saveDesignButton.setAttribute(
        'aria-busy',
        loading ? 'true' : 'false'
      );

      if (uploadStatus) {
        uploadStatus.hidden = !loading;
      }
    }

    function scrollToElement(element) {
      if (!element) {
        return;
      }

      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }

    function focusElement(element) {
      if (
        !element ||
        typeof element.focus !== 'function'
      ) {
        return;
      }

      try {
        element.focus({
          preventScroll: true
        });
      } catch (error) {
        element.focus();
      }
    }

    function showValidationError(
      text,
      elementToScroll,
      elementToFocus
    ) {
      showMessage(text);

      const scrollTarget =
        elementToScroll ||
        elementToFocus ||
        message;

      scrollToElement(scrollTarget);

      if (elementToFocus) {
        window.setTimeout(function () {
          focusElement(elementToFocus);
        }, 250);
      }
    }

    function markInvalid(element) {
      if (!element) {
        return;
      }

      element.classList.add('cbb-invalid');

      if (
        element.matches(
          'input, select, textarea'
        )
      ) {
        element.setAttribute(
          'aria-invalid',
          'true'
        );
      }
    }

    function clearInvalid(element) {
      if (!element) {
        return;
      }

      element.classList.remove('cbb-invalid');

      if (
        element.matches(
          'input, select, textarea'
        )
      ) {
        element.removeAttribute(
          'aria-invalid'
        );
      }
    }

    function clearInvalidElements(elements) {
      elements.forEach(function (element) {
        clearInvalid(element);
      });
    }

    function getElementValue(selector) {
      const element = intake.querySelector(
        selector
      );

      return (
        element &&
        typeof element.value === 'string'
      )
        ? element.value.trim()
        : '';
    }

    function getCheckedConfiguration() {
      const checked = intake.querySelector(
        'input[name="board_configuration"]:checked'
      );

      return checked ? checked.value : '';
    }

    function usesDifferentBoardDesigns() {
      return (
        getCheckedConfiguration() ===
        'Different design on each board'
      );
    }

    // ========================================
    // Wizard Navigation
    // ========================================

    function updateProgress(stepNumber) {
      if (currentStepNumber) {
        currentStepNumber.textContent =
          String(stepNumber);
      }

      progressItems.forEach(function (item) {
        const itemStep = Number(
          item.dataset.progress
        );

        item.classList.toggle(
          'is-active',
          itemStep === stepNumber
        );

        item.classList.toggle(
          'is-complete',
          itemStep < stepNumber
        );
      });

      progressLines.forEach(
        function (line, index) {
          line.classList.toggle(
            'is-complete',
            index + 1 < stepNumber
          );
        }
      );
    }

    function showStep(stepNumber) {
      clearMessage();

      steps.forEach(function (step) {
        const isCurrent =
          Number(step.dataset.step) === stepNumber;

        step.hidden = !isCurrent;

        step.classList.toggle(
          'is-active',
          isCurrent
        );
      });

      updateProgress(stepNumber);

      intake.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }

    // ========================================
    // Board Configuration UI
    // ========================================

    function updateBoardConfigurationUI() {
      const differentDesigns =
        usesDifferentBoardDesigns();

      boardOptions.forEach(function (option) {
        const optionCard = option.closest(
          '.cbb-board-option'
        );

        if (optionCard) {
          optionCard.classList.toggle(
            'is-selected',
            option.checked
          );
        }
      });

      const boardTwoUploadGroup =
        intake.querySelector(
          '#cbb-board2-upload-group'
        );

      const boardTwoNotesGroup =
        intake.querySelector(
          '#cbb-board2-notes-group'
        );

      const boardTwoFileRow =
        intake.querySelector(
          '#cbb-review-board2-file-row'
        );

      const boardTwoNotesRow =
        intake.querySelector(
          '#cbb-review-board2-notes-row'
        );

      if (boardTwoUploadGroup) {
        boardTwoUploadGroup.hidden =
          !differentDesigns;
      }

      if (boardTwoNotesGroup) {
        boardTwoNotesGroup.hidden =
          !differentDesigns;
      }

      if (boardTwoFileRow) {
        boardTwoFileRow.hidden =
          !differentDesigns;
      }

      if (boardTwoNotesRow) {
        boardTwoNotesRow.hidden =
          !differentDesigns;
      }

      /*
       * Board 2 instructions are only relevant
       * when different designs are selected.
       */
      if (!differentDesigns) {
        clearInvalid(boardTwoNotesInput);
      }
    }

    // ========================================
    // File Status UI
    // ========================================

    function updateFileStatus(input) {
      const status = intake.querySelector(
        '[data-file-status="' +
          input.id +
          '"]'
      );

      if (!status) {
        return;
      }

      if (
        !input.files ||
        input.files.length === 0
      ) {
        status.textContent = input.multiple
          ? 'No files selected'
          : 'No file selected';

        status.classList.remove('has-file');

        return;
      }

      if (input.files.length === 1) {
        status.textContent =
          '✓ ' + input.files[0].name;
      } else {
        status.textContent =
          '✓ ' +
          input.files.length +
          ' files selected';
      }

      status.classList.add('has-file');
    }

    // ========================================
    // Review Screen
    // ========================================

    function getFileSummary(selector) {
      const input = intake.querySelector(
        selector
      );

      if (
        !input ||
        !input.files ||
        input.files.length === 0
      ) {
        return 'None provided';
      }

      return Array.from(input.files)
        .map(function (file) {
          return file.name;
        })
        .join(', ');
    }

    function getFieldValue(selector) {
      const input = intake.querySelector(
        selector
      );

      if (
        !input ||
        typeof input.value !== 'string' ||
        !input.value.trim()
      ) {
        return 'None provided';
      }

      return input.value.trim();
    }

    function setReviewText(selector, value) {
      const element = intake.querySelector(
        selector
      );

      if (element) {
        element.textContent = value;
      }
    }

    function buildReview() {
      const configuration =
        getCheckedConfiguration();

      setReviewText(
        '#cbb-review-name',
        getFieldValue('#cbb-customer-name')
      );

      setReviewText(
        '#cbb-review-email',
        getFieldValue('#cbb-customer-email')
      );

      setReviewText(
        '#cbb-review-phone',
        getFieldValue('#cbb-phone')
      );

      setReviewText(
        '#cbb-review-configuration',
        configuration || 'None provided'
      );

      setReviewText(
        '#cbb-review-board1-file',
        getFileSummary('#cbb-board1-photo')
      );

      setReviewText(
        '#cbb-review-board2-file',
        getFileSummary('#cbb-board2-photo')
      );

      setReviewText(
        '#cbb-review-logo-file',
        getFileSummary('#cbb-logo')
      );

      setReviewText(
        '#cbb-review-inspiration-files',
        getFileSummary('#cbb-inspiration')
      );

      setReviewText(
        '#cbb-review-artwork-files',
        getFileSummary('#cbb-artwork')
      );

      setReviewText(
        '#cbb-review-text',
        getFieldValue('#cbb-text-to-include')
      );

      setReviewText(
        '#cbb-review-style',
        getFieldValue('#cbb-design-style')
      );

      setReviewText(
        '#cbb-review-colors',
        getFieldValue('#cbb-color-preferences')
      );

      setReviewText(
        '#cbb-review-board1-notes',
        getFieldValue('#cbb-board1-notes')
      );

      setReviewText(
        '#cbb-review-board2-notes',
        getFieldValue('#cbb-board2-notes')
      );

      setReviewText(
        '#cbb-review-special',
        getFieldValue(
          '#cbb-special-instructions'
        )
      );

      updateBoardConfigurationUI();

      const successMessage = intake.querySelector(
        '#cbb-test-success'
      );

      if (successMessage) {
        successMessage.hidden = true;
      }
    }

    // ========================================
    // Validation
    // ========================================

    function validateBoardConfiguration() {
      clearMessage();
      clearInvalid(boardOptionsFieldset);

      const configuration =
        getCheckedConfiguration();

      if (!configuration) {
        markInvalid(boardOptionsFieldset);

        showValidationError(
          'Please choose whether both boards will use the same design or different designs.',
          boardOptionsFieldset,
          boardOptions[0] || null
        );

        return false;
      }

      return true;
    }

    function validateDesignDetails() {
      clearMessage();

      const designDetailInputs = [
        designStyleInput,
        colorPreferencesInput,
        textToIncludeInput,
        boardOneNotesInput,
        boardTwoNotesInput,
        specialInstructionsInput
      ].filter(Boolean);

      clearInvalidElements(
        designDetailInputs
      );

      const textToInclude = getElementValue(
        '#cbb-text-to-include'
      );

      const designStyle = getElementValue(
        '#cbb-design-style'
      );

      const colorPreferences = getElementValue(
        '#cbb-color-preferences'
      );

      const boardOneNotes = getElementValue(
        '#cbb-board1-notes'
      );

      const boardTwoNotes = getElementValue(
        '#cbb-board2-notes'
      );

      const specialInstructions =
        getElementValue(
          '#cbb-special-instructions'
        );

      const hasUsefulDetails =
        textToInclude ||
        designStyle ||
        colorPreferences ||
        boardOneNotes ||
        boardTwoNotes ||
        specialInstructions;

      if (!hasUsefulDetails) {
        /*
         * Mark the main design fields so the
         * customer can see where information
         * should be entered.
         */
        markInvalid(designStyleInput);
        markInvalid(boardOneNotesInput);

        showValidationError(
          'Please provide at least one design detail, style choice, color preference, or instruction.',
          designStyleInput || boardOneNotesInput,
          designStyleInput || boardOneNotesInput
        );

        return false;
      }

      if (
        usesDifferentBoardDesigns() &&
        !boardTwoNotes
      ) {
        markInvalid(boardTwoNotesInput);

        const boardTwoNotesGroup =
          intake.querySelector(
            '#cbb-board2-notes-group'
          );

        showValidationError(
          'Please provide design instructions for Board 2.',
          boardTwoNotesGroup || boardTwoNotesInput,
          boardTwoNotesInput
        );

        return false;
      }

      return true;
    }

    /*
     * File uploads are intentionally optional.
     * Customers may continue without uploading
     * photos, logos, artwork, or inspiration files.
     */
    function validateFileUploads() {
      clearMessage();
      return true;
    }

    function validateContactInformation() {
      clearMessage();

      if (!customerNameInput || !customerEmailInput) {
        showValidationError(
          'Something went wrong loading the contact step. Please refresh the page and try again.',
          message,
          null
        );

        return false;
      }

      clearInvalid(customerNameInput);
      clearInvalid(customerEmailInput);

      if (!customerNameInput.value.trim()) {
        markInvalid(customerNameInput);

        showValidationError(
          'Please enter your name.',
          customerNameInput,
          customerNameInput
        );

        return false;
      }

      if (
        !customerEmailInput.value.trim() ||
        !customerEmailInput.validity.valid
      ) {
        markInvalid(customerEmailInput);

        showValidationError(
          'Please enter a valid email address so we can send your design proof.',
          customerEmailInput,
          customerEmailInput
        );

        return false;
      }

      return true;
    }

    function validateBoardSizeConfirmation() {
      clearMessage();

      clearInvalid(boardSizeConfirmedInput);
      clearInvalid(confirmationBox);

      if (
        !boardSizeConfirmedInput ||
        !boardSizeConfirmedInput.checked
      ) {
        markInvalid(boardSizeConfirmedInput);
        markInvalid(confirmationBox);

        showValidationError(
          'Please confirm that your boards have a 24-inch by 48-inch playing surface.',
          confirmationBox || boardSizeConfirmedInput,
          boardSizeConfirmedInput
        );

        return false;
      }

      return true;
    }

    // ========================================
    // Submission ID and FormData
    // ========================================

    function createSubmissionId() {
      if (
        window.crypto &&
        typeof window.crypto.randomUUID ===
          'function'
      ) {
        return window.crypto.randomUUID();
      }

      return (
        'cbb-' +
        Date.now() +
        '-' +
        Math.random()
          .toString(16)
          .slice(2)
      );
    }

    function appendFiles(
      formData,
      inputSelector,
      fieldName,
      multiple
    ) {
      const input = intake.querySelector(
        inputSelector
      );

      if (
        !input ||
        !input.files ||
        input.files.length === 0
      ) {
        return;
      }

      Array.from(input.files).forEach(
        function (file, index) {
          const key = multiple
            ? fieldName + '_' + (index + 1)
            : fieldName;

          formData.append(
            key,
            file,
            file.name
          );
        }
      );
    }

    function buildSubmissionFormData() {
      const formData = new FormData();

      const submissionIdInput =
        intake.querySelector(
          '#cbb-submission-id'
        );

      let submissionId = submissionIdInput
        ? submissionIdInput.value
        : '';

      if (!submissionId) {
        submissionId = createSubmissionId();

        if (submissionIdInput) {
          submissionIdInput.value =
            submissionId;
        }
      }

      formData.append(
        'submission_id',
        submissionId
      );

      formData.append(
        'customer_name',
        getElementValue('#cbb-customer-name')
      );

      formData.append(
        'customer_email',
        getElementValue('#cbb-customer-email')
      );

      formData.append(
        'phone',
        getElementValue('#cbb-phone')
      );

      formData.append(
        'board_configuration',
        getCheckedConfiguration()
      );

      formData.append(
        'text_to_include',
        getElementValue('#cbb-text-to-include')
      );

      formData.append(
        'design_style',
        getElementValue('#cbb-design-style')
      );

      formData.append(
        'color_preferences',
        getElementValue(
          '#cbb-color-preferences'
        )
      );

      formData.append(
        'board1_notes',
        getElementValue('#cbb-board1-notes')
      );

      formData.append(
        'board2_notes',
        getElementValue('#cbb-board2-notes')
      );

      formData.append(
        'special_instructions',
        getElementValue(
          '#cbb-special-instructions'
        )
      );

      formData.append(
        'board_size_confirmed',
        'Yes'
      );

      appendFiles(
        formData,
        '#cbb-board1-photo',
        'board1_photo',
        false
      );

      appendFiles(
        formData,
        '#cbb-board2-photo',
        'board2_photo',
        false
      );

      appendFiles(
        formData,
        '#cbb-logo',
        'logo',
        false
      );

      appendFiles(
        formData,
        '#cbb-inspiration',
        'inspiration',
        true
      );

      appendFiles(
        formData,
        '#cbb-artwork',
        'artwork',
        true
      );

      return formData;
    }

    // ========================================
    // Shopify Cart Integration
    // ========================================

    /*
     * The Design Studio must receive the correct
     * Shopify variant ID from its Liquid markup.
     *
     * Required Liquid attribute:
     * data-variant-id="{{ product.selected_or_first_available_variant.id }}"
     */
    function getSelectedVariantId() {
      const variantId =
        intake.dataset.variantId
          ? intake.dataset.variantId.trim()
          : '';

      if (!variantId) {
        throw new Error(
          'The custom cornhole board product could not be identified. Please refresh the page and try again.'
        );
      }

      if (!/^\d+$/.test(variantId)) {
        throw new Error(
          'The custom cornhole board product has an invalid Shopify variant ID.'
        );
      }

      return variantId;
    }

    function addDesignToCart(designId) {
      const variantId =
        getSelectedVariantId();

      const configuration =
        getCheckedConfiguration();

      const customerName =
        getElementValue(
          '#cbb-customer-name'
        );

      const submissionId =
        getElementValue(
          '#cbb-submission-id'
        );

      const cartRoot =
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';

      return fetch(
        cartRoot + 'cart/add.js',
        {
          method: 'POST',
          headers: {
            'Content-Type':
              'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: {
                  'Design ID': designId,
                  'Board Configuration':
                    configuration,
                  'Customer Name':
                    customerName,
                  'Design Status':
                    'Design Request Received',
                  '_Submission ID':
                    submissionId
                }
              }
            ]
          })
        }
      ).then(function (response) {
        if (!response.ok) {
          return response
            .json()
            .catch(function () {
              return null;
            })
            .then(function (errorBody) {
              throw new Error(
                (
                  errorBody &&
                  errorBody.description
                ) ||
                (
                  errorBody &&
                  errorBody.message
                ) ||
                'The product could not be added to your cart.'
              );
            });
        }

        return response.json();
      });
    }

    // ========================================
    // Step Navigation Events
    // ========================================

    const stepOneNextButton =
      intake.querySelector(
        '#cbb-step-one-next'
      );

    if (stepOneNextButton) {
      stepOneNextButton.addEventListener(
        'click',
        function () {
          if (
            !validateBoardConfiguration()
          ) {
            return;
          }

          updateBoardConfigurationUI();
          showStep(STEPS.DESIGN);
        }
      );
    }

    const stepTwoNextButton =
      intake.querySelector(
        '#cbb-step-two-next'
      );

    if (stepTwoNextButton) {
      stepTwoNextButton.addEventListener(
        'click',
        function () {
          if (!validateDesignDetails()) {
            return;
          }

          updateBoardConfigurationUI();
          showStep(STEPS.UPLOADS);
        }
      );
    }

    const stepThreeNextButton =
      intake.querySelector(
        '#cbb-step-three-next'
      );

    if (stepThreeNextButton) {
      stepThreeNextButton.addEventListener(
        'click',
        function () {
          if (!validateFileUploads()) {
            return;
          }

          showStep(STEPS.CONTACT);
        }
      );
    }

    const stepFourNextButton =
      intake.querySelector(
        '#cbb-step-four-next'
      );

    if (stepFourNextButton) {
      stepFourNextButton.addEventListener(
        'click',
        function () {
          if (
            !validateContactInformation()
          ) {
            return;
          }

          buildReview();
          showStep(STEPS.REVIEW);
        }
      );
    }

    intake
      .querySelectorAll('[data-back-step]')
      .forEach(function (button) {
        button.addEventListener(
          'click',
          function () {
            const targetStep = Number(
              button.dataset.backStep
            );

            if (!targetStep) {
              return;
            }

            updateBoardConfigurationUI();

            if (
              targetStep === STEPS.REVIEW
            ) {
              buildReview();
            }

            showStep(targetStep);
          }
        );
      });

    // ========================================
    // Board Option Events
    // ========================================

    boardOptions.forEach(function (radio) {
      radio.addEventListener(
        'change',
        function () {
          clearInvalid(boardOptionsFieldset);
          clearMessage();
          updateBoardConfigurationUI();
        }
      );
    });

    // ========================================
    // File Input Events
    // ========================================

    const fileInputs = intake.querySelectorAll(
      'input[type="file"]'
    );

    fileInputs.forEach(function (input) {
      input.addEventListener(
        'change',
        function () {
          updateFileStatus(input);
        }
      );
    });

    // ========================================
    // Remove Invalid State After Editing
    // ========================================

    const generalDesignInputs = [
      designStyleInput,
      colorPreferencesInput,
      textToIncludeInput,
      boardOneNotesInput,
      specialInstructionsInput
    ].filter(Boolean);

    generalDesignInputs.forEach(function (input) {
      const eventName =
        input.tagName === 'SELECT'
          ? 'change'
          : 'input';

      input.addEventListener(
        eventName,
        function () {
          /*
           * Once any useful design detail exists,
           * clear the grouped design warning.
           */
          const hasUsefulDetails =
            getElementValue(
              '#cbb-text-to-include'
            ) ||
            getElementValue(
              '#cbb-design-style'
            ) ||
            getElementValue(
              '#cbb-color-preferences'
            ) ||
            getElementValue(
              '#cbb-board1-notes'
            ) ||
            getElementValue(
              '#cbb-board2-notes'
            ) ||
            getElementValue(
              '#cbb-special-instructions'
            );

          if (hasUsefulDetails) {
            clearInvalid(designStyleInput);
            clearInvalid(boardOneNotesInput);
            clearMessage();
          }

          clearInvalid(input);
        }
      );
    });

    if (boardTwoNotesInput) {
      boardTwoNotesInput.addEventListener(
        'input',
        function () {
          if (boardTwoNotesInput.value.trim()) {
            clearInvalid(boardTwoNotesInput);
            clearMessage();
          }
        }
      );
    }

    if (customerNameInput) {
      customerNameInput.addEventListener(
        'input',
        function () {
          if (customerNameInput.value.trim()) {
            clearInvalid(customerNameInput);
            clearMessage();
          }
        }
      );
    }

    if (customerEmailInput) {
      customerEmailInput.addEventListener(
        'input',
        function () {
          if (
            customerEmailInput.value.trim() &&
            customerEmailInput.validity.valid
          ) {
            clearInvalid(customerEmailInput);
            clearMessage();
          }
        }
      );
    }

    if (boardSizeConfirmedInput) {
      boardSizeConfirmedInput.addEventListener(
        'change',
        function () {
          if (boardSizeConfirmedInput.checked) {
            clearInvalid(
              boardSizeConfirmedInput
            );

            clearInvalid(
              confirmationBox
            );

            clearMessage();
          }
        }
      );
    }

    // ========================================
    // n8n Submission and Shopify Cart Flow
    // ========================================

    if (saveDesignButton) {
      saveDesignButton.addEventListener(
        'click',
        async function () {
          clearMessage();

          if (
            !validateBoardSizeConfirmation()
          ) {
            return;
          }

          const successMessage =
            intake.querySelector(
              '#cbb-test-success'
            );

          const returnedDesignId =
            intake.querySelector(
              '#cbb-returned-design-id'
            );

          saveDesignButton.disabled = true;

          /*
           * When n8n has already returned a Design ID,
           * retries only attempt the Shopify cart
           * operation. The webhook is not called again.
           */
          let designId =
            saveDesignButton.dataset.designId ||
            '';

          if (!designId) {
            if (successMessage) {
              successMessage.hidden = true;
            }

            setSaveButtonState(
              'Saving Your Design...',
              true
            );

            try {
              const formData =
                buildSubmissionFormData();

              const response = await fetch(
                'https://custombagboards.app.n8n.cloud/webhook/cbb-design-intake',
                {
                  method: 'POST',
                  body: formData
                }
              );

              let result;

              try {
                result =
                  await response.json();
              } catch (jsonError) {
                throw new Error(
                  'The server returned an invalid response. Please try again.'
                );
              }

              if (
                !response.ok ||
                !result.success ||
                !result.design_id
              ) {
                throw new Error(
                  result.error ||
                  result.message ||
                  'Your design request could not be saved.'
                );
              }

              designId = result.design_id;

              saveDesignButton.dataset.designId =
                designId;

              if (returnedDesignId) {
                returnedDesignId.textContent =
                  designId;
              }

              if (successMessage) {
                successMessage.hidden = false;

                successMessage.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
              }
            } catch (error) {
              console.error(
                'Custom Bag Boards submission error:',
                error
              );

              showValidationError(
                error.message ||
                'Something went wrong while saving your design. Please try again.',
                message,
                null
              );

              saveDesignButton.disabled = false;

              setSaveButtonState(
                'Save Design and Add to Cart',
                false
              );

              return;
            }
          }

          /*
           * A Design ID now exists, either from this
           * submission or a previous attempt. Only add
           * the correct product variant to the cart.
           */
          setSaveButtonState(
            'Adding To Cart...',
            true
          );

          try {
            await addDesignToCart(designId);

            setSaveButtonState(
              'Added To Cart',
              false
            );

            window.location.href =
              '/cart';
          } catch (error) {
            console.error(
              'Custom Bag Boards add to cart error:',
              error
            );

            showValidationError(
              error.message ||
              'Your design was saved, but the item could not be added to your cart. Please try again.',
              message,
              null
            );

            saveDesignButton.disabled = false;

            setSaveButtonState(
              'Retry Add to Cart',
              false
            );
          }
        }
      );
    }

    // ========================================
    // Initial UI State
    // ========================================

    updateBoardConfigurationUI();
    updateProgress(STEPS.BOARDS);
  }

  // ========================================
  // Initialization
  // ========================================

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeCbbDesignStudio
    );
  } else {
    initializeCbbDesignStudio();
  }

  document.addEventListener(
    'shopify:section:load',
    initializeCbbDesignStudio
  );
})();